import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authApiService } from '../services/authApi';

interface UserMenuProps {
  user: {
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
    isAdmin?: boolean;
  };
  onLogout: () => void;
  onShowDiscussions: () => void;
  onShowDashboardAdmin?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout, onShowDiscussions, onShowDashboardAdmin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fermer le menu si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authApiService.logout();
      onLogout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Déconnecter quand même côté client
      onLogout();
    }
  };

  const menuItems = user.isAdmin ? [
    // Menu Admin uniquement
    {
      icon: '⚙️',
      label: 'Dashboard Admin',
      action: () => {
        navigate('/admin');
        setIsOpen(false);
      }
    },
    {
      icon: '👥',
      label: 'Utilisateurs',
      action: () => {
        navigate('/users');
        setIsOpen(false);
      }
    },
    {
      icon: '💬',
      label: 'Messages',
      action: () => {
        navigate('/messages');
        setIsOpen(false);
      }
    },
    {
      icon: '🛒',
      label: 'Demandes Clients',
      action: () => {
        navigate('/demandesclients');
        setIsOpen(false);
      }
    },
    {
      icon: '⚙️',
      label: 'Gestion Projets',
      action: () => {
        navigate('/gestionprojets');
        setIsOpen(false);
      }
    },
    {
      icon: '📋',
      label: 'Historique',
      action: () => {
        navigate('/historique');
        setIsOpen(false);
      }
    },
    {
      icon: '🏢',
      label: 'Profil Entreprise',
      action: () => {
        navigate('/company-profile');
        setIsOpen(false);
      }
    },
    {
      icon: '🚪',
      label: 'Déconnexion',
      action: handleLogout,
      isDanger: true
    }
  ] : [
    // Menu Utilisateur standard
    {
      icon: '👤',
      label: 'Mon Profil',
      action: () => {
        navigate('/profile');
        setIsOpen(false);
      }
    },
    {
      icon: '🎁',
      label: 'Mes Promos',
      action: () => {
        navigate('/promowheel');
        setIsOpen(false);
      }
    },
    {
      icon: '📄',
      label: 'Mes factures',
      action: () => {
        navigate('/factures');
        setIsOpen(false);
      }
    },
    {
      icon: '📁',
      label: 'Mes projets',
      action: () => {
        navigate('/projets');
        setIsOpen(false);
      }
    },
    {
      icon: '🚪',
      label: 'Déconnexion',
      action: handleLogout,
      isDanger: true
    }
  ];

  return (
    <div className="relative" ref={menuRef}>
      {/* Bouton Mon espace */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-1.5 text-amber-200 hover:text-amber-100 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300"
      >
        <div className="w-6 h-6 rounded-full flex items-center justify-center text-amber-100 font-medium text-xs border border-amber-400/30">
          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
        </div>
        <span className="hidden sm:inline">Mon espace</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      {/* Menu déroulant */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border border-amber-300/30 overflow-hidden z-50"
          >
            {/* En-tête du menu avec gradient gold */}
            <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-200/50">
              <p className="font-semibold text-gray-800 text-sm">{user.firstName} {user.lastName}</p>
              <p className="text-xs text-gray-600 truncate">{user.email}</p>
            </div>

            {/* Items du menu */}
            <div className="py-2">
              {menuItems.map((item, index) => {
                const isDanger = 'isDanger' in item && item.isDanger;
                const isLastItem = index === menuItems.length - 1;
                
                return (
                  <div key={index}>
                    {isLastItem && (
                      <div className="mx-3 my-2 border-t border-gray-200" />
                    )}
                    <button
                      onClick={item.action}
                      className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-all duration-200 group ${
                        isDanger
                          ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                          : 'text-gray-700 hover:bg-amber-50 hover:text-amber-900'
                      }`}
                    >
                      <span className="text-lg group-hover:scale-110 transition-transform duration-200">{item.icon}</span>
                      <span className="flex-1 text-sm font-light">{item.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserMenu;
